import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { TWebsiteBuilderElementParentNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import React, { useEffect, useMemo, useState } from "react";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/websiteBuilder";
import { IWebsiteBuilderElementOperations } from "pages/secure/facility/websiteBuilder/editor/hooks/elementOperations/useWebsiteBuilderElementOperations";
import {
  isWebsiteBuilderStretchElementData,
  IWebsiteBuilderElementStretch,
  TWebsiteBuilderElement,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { IWebsiteBuilderClipboard } from "pages/secure/facility/websiteBuilder/editor/hooks/clipboard/useWebsiteBuilderClipboard";
import { TWebsiteBuilderTreeRefMap } from "pages/secure/facility/websiteBuilder/editor/hooks/treeRefMap/useTreeRefMapRegistration";
import {
  IWebsiteBuilderContextMenuPosition,
  IWebsiteBuilderContextMenuOption,
  WebsiteBuilderContextMenu,
} from "../../helpers/WebsiteBuilderContextMenu";
import {
  canBringToBackElementInSection,
  canBringToFrontElementInSection,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { StretchSettings } from "./settings/StretchSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { IWebsiteBuilderPopupContent } from "../../helpers/popup/WebsiteBuilderPopup";

export interface IUseElementViewContextMenuArgs {
  node: TWebsiteBuilderElement;
  parentNode: TWebsiteBuilderElementParentNode;
  elementOperations: IWebsiteBuilderElementOperations;
  clipboard: IWebsiteBuilderClipboard;
  selected: boolean;
  menuActive: boolean;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

type TElementViewContextMenuPopupView = "none" | "stretch";

interface IElementViewContextMenuState {
  popupView: TElementViewContextMenuPopupView;
  position: IWebsiteBuilderContextMenuPosition;
  domRect: DOMRect;
}

const initialState: IElementViewContextMenuState = {
  popupView: "none",
  position: null,
  domRect: null,
};

interface IUseElementViewContextMenu {
  viewContextMenu: JSX.Element;
  handleViewRightClick: (position: IWebsiteBuilderContextMenuPosition) => void;
}

export function useElementViewContextMenu(args: IUseElementViewContextMenuArgs): IUseElementViewContextMenu {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<IElementViewContextMenuState>(cloneDeep(initialState));

  const modifierKey = window.navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";

  const bringToFrontElementDisabled =
    args.parentNode.type !== "section" || !canBringToFrontElementInSection(args.node, args.parentNode);

  const bringToBackElementDisabled =
    args.parentNode.type !== "section" || !canBringToBackElementInSection(args.node, args.parentNode);

  useEffect(() => {
    if (!args.menuActive) {
      setState(cloneDeep(initialState));
    } else {
      const domRect = args.treeRefMap.current.get(args.node.id)?.getBoundingClientRect();

      setState(prev => ({ ...prev, domRect }));
    }
  }, [args.menuActive, args.node.id]);

  const elementStretch: IWebsiteBuilderElementStretch = useMemo(() => {
    if (isWebsiteBuilderStretchElementData(args.node.element)) {
      return args.node.element.stretch;
    } else {
      return null;
    }
  }, [args.node.element]);

  const popupContent: IWebsiteBuilderPopupContent = useMemo(() => {
    if (state.popupView === "stretch") {
      return {
        heading: t("Stretch Settings"),
        render: () => <StretchSettings id={args.node.id} stretch={elementStretch} />,
      };
    } else {
      return null;
    }
  }, [state.popupView, args.node.id, elementStretch]);

  const creationMenuOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Copy"),
      shortcut: `${modifierKey} + C`,
      onClick: () => args.elementOperations.copyElement(args.node),
    },
    {
      label: t("Paste"),
      shortcut: `${modifierKey} + V`,
      onClick: () => args.elementOperations.pasteAtElement(args.node, args.parentNode),
      disabled: !args.clipboard.hasElement,
    },
    {
      label: t("Duplicate"),
      shortcut: `${modifierKey} + D`,
      onClick: () => args.elementOperations.duplicateElement(args.node, args.parentNode),
    },
  ];

  const zLayerMenuOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Bring to front"),
      shortcut: (
        <>
          {modifierKey} + <FontAwesomeIcon icon={faArrowUp} />
        </>
      ),
      onClick: () => args.elementOperations.bringToFrontElement(args.node, args.parentNode),
      disabled: bringToFrontElementDisabled,
    },
    {
      label: t("Bring to back"),
      shortcut: (
        <>
          {modifierKey} + <FontAwesomeIcon icon={faArrowDown} />
        </>
      ),
      onClick: () => args.elementOperations.bringToBackElement(args.node, args.parentNode),
      disabled: bringToBackElementDisabled,
    },
  ];

  const deletionMenuOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Delete"),
      shortcut: "Del",
      onClick: () => args.elementOperations.deleteElement(args.node, args.parentNode),
    },
    {
      label: t("Cut"),
      shortcut: `${modifierKey} + X`,
      onClick: () => args.elementOperations.cutElement(args.node, args.parentNode),
    },
  ];

  function getSections() {
    const sections: IWebsiteBuilderContextMenuOption[][] = [];

    if (elementStretch) {
      sections.push([
        {
          label: t("Stretch"),
          shortcut: "",
          onClick: () => setState(prev => ({ ...prev, popupView: "stretch" })),
          stayOpenAfterClick: true,
        },
      ]);
    }

    sections.push(creationMenuOptions, zLayerMenuOptions, deletionMenuOptions);

    return sections;
  }

  const sections = getSections();

  function closeMenu() {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          editingMode: "none",
        },
      }),
    );
  }

  function handleViewRightClick(position: IWebsiteBuilderContextMenuPosition) {
    if (!args.selected) {
      return;
    }

    if (!args.menuActive) {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            editingMode: "element-context-menu",
          },
        }),
      );
    }

    setState(prev => ({
      ...prev,
      position,
    }));
  }

  function renderContextMenu() {
    if (!args.menuActive || state.position == null) {
      return null;
    } else {
      return (
        <WebsiteBuilderContextMenu
          domRect={state.domRect}
          showPopup={state.popupView !== "none"}
          popupContent={popupContent}
          position={state.position}
          sections={sections}
          treeRefMap={args.treeRefMap}
          closeMenu={closeMenu}
        />
      );
    }
  }

  return {
    viewContextMenu: renderContextMenu(),
    handleViewRightClick,
  };
}
